/**
 * messaging.service.ts
 * Copyright © 2016-2018, My Coverage Plan, Inc.
 * All rights reserved.
 */

/**
 * @author Taylor DH Smith <tdsmith@safetyweb.org>
 */

import { Injectable } from "@angular/core";
import {
  BehaviorSubject,
  EmptyError,
  Notification,
  Observable,
  Observer,
  ReplaySubject,
  Subject,
  Subscription
} from "rxjs/Rx";
import { BuildFlags } from "../configuration/build-flags";
import { ConfigurationService } from "../configuration/configuration.service";
import { MessageSender } from "./message-sender";
import { DisconnectUpstreamMessage } from "./messages/auth/";
import {
  AcknowledgmentResponse,
  BadReplyError,
  ConnectionFailureMessage,
  InternalMessage,
  Message,
  MessageTag,
  MissingReplyError,
  ReplyMessage,
  ReplyTimeoutError,
  ReplyVerifyingMessage,
  PingRequest
} from "./messages/general/";
import { Identifiable, Tagged } from "./messages/index";
import { MessageValidator } from "./validation/message-validator";

import {
  CompositeConnection,
  Connection,
  DownloadSocket,
  EMPTY,
  MockConnection,
  SocketFile,
  WebSocketConnection
} from "./connection/index";

import { AlertService } from "../common/alert.service";
import { escapeForHTML, oneline } from "../support/utilities/functions";
import { RingBuffer } from "../support/utilities/ring-buffer";
import { ProtocolVersionService } from "./protocol-version.service";
import { SettingService } from "app/configuration/setting.service";
import { local } from "d3-selection";

// The amount of time until the client treats a nonresponse to a reply-expecting
// message as an error in that conversation.
const DEFAULT_REPLY_TIMEOUT : number = 3000;
// We allow negotiation more time because the browser may include the initial
// handshake in the time taken for the first message.
const NEGOTIATION_TIMEOUT : number = 6000;
// TODO: move to configuration (or LocalSettings)

/**
 * Status of the {@link MessagingService} for purposes of preventing duplicate
 * or illegal actions and debugging mis-timed behavior.
 */
export enum MessagingState
{
  Starting, // only used for the first start
  Negotiating,
  Ready,
  Restarting,
  Dead
}

function stateName (m : MessagingState) : string
{
  switch (m)
  {
    case MessagingState.Starting:
      return "not yet connected";
    case MessagingState.Negotiating:
      return "negotiating";
    case MessagingState.Ready:
      return "connected";
    case MessagingState.Restarting:
      return "reconnecting";
    case MessagingState.Dead:
      return "disconnected";
  }
}

enum MessagingFailureType
{
  // problems with the initial socket set-up
  FeatureAbsence,
  SocketCreationFailure,
  RemoteAddressPermissions,
  NegotiationFailure,

  AdminAction,
  BadData,

  Unknown
}

// TypeScript keeps defaulting to the node definition
declare var setTimeout : (fn : () => void, t : number) => number;

/**
 * The {@link MessagingService} handles all of the communication with the server
 * via the configured {@link Connection}. In general, the incoming and outgoing
 * streams are managed via Observables that downstream services can subscribe to
 * or filter/transform/etc.
 */
@Injectable()
export class MessagingService implements MessageSender
{
  /**
   * The {@link Connection} used to communicate. For now it is hard-set to
   * a {@link WebSocketConnection} since we don't have a concept of other types.
   */
  public connection : Connection<Message>;

  public mockInput? : MockConnection<Message>;

  /**
   * A flag indicating whether we are currently attempting a retry.
   */
  public state : MessagingState = MessagingState.Starting;

  /**
   * A boolean timeline of the service's readiness, generally indicating whether
   * the {@link Connection} is alive or dead. (Note that it's a BehaviorSubject,
   * so it will always emit the most recent value to new subscribers.)
   */
  public ready : Subject<boolean> = new BehaviorSubject<boolean>(false);

  /**
   * The {@link Message}s being emitted by the {@link Connection}.
   */
  private incoming : Observable<Message>;

  /**
   * The subscription to the incoming message stream that triggers retry events.
   */
  private subscription : Subscription;

  /**
   * A (debugging) counter to identify which connection is which.
   */
  private connectionId : number = 1;

  /**
   * The recentLog holds timestamped copies of the last N messages.
   */
  public recentLog : RingBuffer<Message> = new RingBuffer<Message>(30);

  /**
   * The URL used for the websocket connection.
   */
  public socketUrl : string;

  public recentPingsRTT : RingBuffer<number> = new RingBuffer<number>(10);
  private lastPingSuccessful : boolean | null = null;

  public pingStats () : [number, number]
  {
    const pings : number[] = this.recentPingsRTT.list();
    const n : number = pings.length;
    if (n === 0)
    {
      return [1, Number.MAX_SAFE_INTEGER];
    }
    const mean : number = pings.reduce((a, b) => a + b) / n;
    return [
      mean,
      pings.reduce((prev, current) => prev + Math.pow(current - mean, 2))
       / (n - 1)];
  }
  private checkDeficientPing (newPing : number) : void
  {
    const minimumForCheck : number = 5;
    const varianceThreshold : number = Math.sqrt(3);
    const msRequiredForAlert : number = 50;
    if (this.recentPingsRTT.size() < minimumForCheck)
    {
      return;
    }
    const [mean, variance] = this.pingStats();
    if (newPing > msRequiredForAlert
      && Math.abs((newPing - mean) / variance) > varianceThreshold)
    {
      // The new ping is greater than 3 std dev from our mean
      this.alerts.warnAndReplace(
        oneline`You may be experiencing connection issues. (Your last heartbeat
          took ${newPing}ms compared to an average of ${mean}ms.)`,
        "slowPing");
    }
  }

  /**
   * Construct a new {@link MessagingService}.
   */
  constructor (
    protected config : ConfigurationService,
    private alerts : AlertService,
    private protocolVersionService : ProtocolVersionService,
    private settings : SettingService
  ) {
    this.buildUrl();
    this.startConnection();
    this.ready.subscribe(
      (b : boolean) => {
        // TODO: DEBUG notifications, remove me!
        console.info(b ? "READY" : "NOT READY");
        if (!b)
        {
          // TODO: move to function
          this.protocolVersionService.reset();
          this.handledTags = [];
        }
      });

    const heartbeat : number = this.settings.get("connection heartbeat");
    if (heartbeat > 0)
    {
      setInterval(() => {
        if (this.state === MessagingState.Ready)
        {
          const pingSent : number = Date.now();
          this.send(new PingRequest()).subscribe((r : Message) => {
            this.lastPingSuccessful = AcknowledgmentResponse.isOk(r);
            if (this.lastPingSuccessful)
            {
              const RTT : number = Date.now() - pingSent;
              this.checkDeficientPing(RTT);
              this.recentPingsRTT.add(RTT);
              // console.log(`Ping response received, took ${RTT}ms`);
            }
          });
        }
      }, heartbeat * 60000);
    }
  }

  /**
   * Use configuration values and location inspection to construct the URL for
   * connecting to the server by websocket.
   */
  private buildUrl () : void
  {
    const host : string = this.config.protocol.socketHost || location.hostname;
    const protocol : string = this.config.protocol.socketProtocol ||
      ((location.protocol === "https:") ? "wss:" : "ws:");
    const port : number = (
      BuildFlags.useProductionHostValues
        ? this.config.protocol.socketPort
        : this.config.protocol.devSocketPort);
    this.socketUrl = `${protocol}//${host}:${port}/`;
  }

  /**
   * Transition to another internal state, assuming the transition is allowed.
   */
  private be (state : MessagingState) : MessagingState
  {
    console.warn(
      `attempting state: ${stateName(this.state)}→${stateName(state)}`);
    if (this.state === state)
    {
      // No change
      return state;
    }
    if (this.state === MessagingState.Dead || state === MessagingState.Starting)
    {
      // We can't leave the dead state and we can't return to the starting state
      throw new Error("MessagingService attempted an illegal state transition");
    }
    if (this.state === MessagingState.Ready)
    {
      // We're LEAVING the ready state.
      this.ready.next(false);
      return this.state = state;
    }
    if (state === MessagingState.Ready)
    {
      // We're ENTERING the ready state.
      this.ready.next(true);
      return this.state = state;
    }
  }

  /**
   * An internal indicator that we should restart the connection automatically.
   */
  private retryOnFail : boolean = true; // TODO source from config

  /**
   * Defines the range of values above and below the average retry delay that
   * the delay length will be randomly picked from.
   */
  public retryDelayRandomization : number = 0;

  /**
   * The number of seconds to wait before retrying the connection.
   */
  private _retryDelay : number = 5; // TODO: source from config
  public get retryDelay () : number
  {
    if (!this.retryDelayRandomization)
    {
      return this._retryDelay;
    }
    return Math.ceil(
      this._retryDelay
      - this.retryDelayRandomization
      + 2 * Math.random() * this.retryDelayRandomization
    );
  }

  private _deadRetryDelay : number = 15;
  private _deadRetryExponent : number = 0;
  public get deadRetryDelay () : number
  {
    return this._deadRetryDelay * Math.pow(2, this._deadRetryExponent++);
  }

  private retryTimeout : number;

  /**
   * The maximum number of retry attempts allowed in one session.
   */
  public retryLimit : number = 5; // TODO: source from config

  /**
   * The number of retries already attempted.
   */
  public retryCount : number = 0;

  private buildConnection () : void
  {
    const trueConnection : Connection<Message> = new WebSocketConnection(
        this.socketUrl,
        new DisconnectUpstreamMessage(this.nextId),
        this.connectionId++
    );
    if (BuildFlags.debugMode)
    {
      this.mockInput = new MockConnection<Message>();
      this.connection = new CompositeConnection([
          new MessageValidator(trueConnection),
          new MessageValidator(this.mockInput)
      ]);
    }
    else
    {
      this.connection = new MessageValidator(trueConnection);
    }
    this.incoming = this.connection.incoming;
  }

  /**
   * Launch a connection and hook up to it.
   */
  private startConnection () : void
  {
    console.info("Starting websocket connection #" + this.connectionId);

    // Create a new connection and grab its incoming message stream
    this.buildConnection();

    // If the incoming messages finish, we assume the connection has closed,
    // so run this.incomingComplete to wrap up/restart appropriately.
    this.subscription = this.incoming.subscribe(
      (message : Message) => null,
      (error : any) => this.incomingError(error),
      () => this.incomingComplete()
    );

    // Set up the recent log to receive all messages, including meta disconnect
    // messages and
    const logSub : Subscription = this.incoming.materialize().map(
      (event : Notification<Message>) => (event.kind === "N")
        ? Object.assign({timestamp: Date.now()}, event.value)
        : new InternalMessage("Socket closed")
    ).subscribe((m : Message) => this.recentLog.add(m));

    // We may wish to track all "non-conversational" messages for debug purposes
    // (because they have to be deliberately receive()'d by services, so it's
    // much easier for them to fall through the cracks), so filter all of the
    // messages with server-issued (negative) indices and log them somehow.
    const unhandledSub : Subscription = this.incoming
      .filter((m : Message) =>
        (m.id < 0) && (this.handledTags.indexOf(m.tag) === -1))
      .subscribe((m : Message) => this.nonconversational(m));

    this.subscription.add(unhandledSub).add(logSub);

    // Begin the negotiation process
    this.negotiate();
  }

  /**
   * Hand the new connection off to the {@link ProtocolVersionService} to
   * perform version negotiation, and transition to ready or dead based on its
   * success. (We consider negotiation failures good cause to halt all future
   * connection attempts.)
   */
  private negotiate () : void
  {
    this.be(MessagingState.Negotiating);

    // Version negotiation happens before ready, so we must call the service
    // deliberately.
    const attempt : Observable<boolean> =
      this.protocolVersionService.negotiate((m : Message) => {
        m.meta.timeout = NEGOTIATION_TIMEOUT;
        return this.send(m);
      });

    attempt.subscribe((result : boolean) =>
      result ? this.be(MessagingState.Ready) : this.be(MessagingState.Dead));
  }

  /**
   * Unsubscribe from a connection, close it, and replace its reference with an
   * empty implementation.
   */
  private releaseConnection () : void
  {
    this.subscription.unsubscribe();
    if (!this.connection.dead)
    {
      this.connection.close();
    }
    this.connection = EMPTY; // error out on new sends and closes
  }

  /**
   * React to a closed connection, by logging and retrying.
   */
  private incomingComplete () : void
  {
    this.alerts.errorAndReplace(oneline`
      You have been disconnected from the server!
      If you have any unsaved work below, consider copying it elsewhere for use
      when you reconnect.`,
      "disconnect");

    console.log("Websocket incoming stream has ended.");
    if (this.retryOnFail)
    {
      this.retryConnection();
    }
  }

  /**
   * React to a connection error. Currently this should only indicate that the
   * connection never successfully opened.
   */
  private incomingError (reason? : any) : void
  {
    if (reason instanceof ConnectionFailureMessage)
    {
      this.retryConnection(true);
    }
    else
    {
      throw new Error("Internal stream threw error (unexpected behavior)");
    }
  }

  /**
   * Prepare a retry attempt, which may happen immediately or after a delay, or
   * not at all if we have hit out attempt limit.
   */
  private retryConnection (wasInitial : boolean = false) : void
  {
    if (this.state === MessagingState.Restarting)
    {
      // we're already restarting (perhaps due to timing of program flow and
      // socket closure), so just return.
      return;
    }

    this.be(MessagingState.Restarting);
    this.releaseConnection();

    this.retryCount++;
    if (this.retryCount >= this.retryLimit)
    {
      this.alerts.error(
        "Connection retry limit reached! You must restart to try again.");
      this.be(MessagingState.Dead);

      return;
    }

    const preamble : string = wasInitial
      ? "Could not connect to the server; retrying"
      : "Connection closed; restarting";
    const delay : number = wasInitial
      ? this.deadRetryDelay
      : this.retryDelay;
    if (delay === 0)
    {
      this.alerts.warn(
        preamble + " [attempt #" + this.retryCount + "]");
      this.startConnection();
    }
    else
    {
      this.alerts.add({
        type: "countdown",
        timeout: delay * 1000,
        message : preamble + " in " + delay
          + " seconds [attempt #" + this.retryCount + "]",
        action : () => this.restart(false),
        actionLabel: "Restart now"
      });
      this.retryTimeout = setTimeout(
        () => this.startConnection(), delay * 1000);
    }
  }

  /**
   * Kill the connection and do not reattempt it.
   */
  public abort () : void
  {
    this.be(MessagingState.Dead);
    this.releaseConnection();
  }

  public restartCount : number = 0;
  public lastRestart : Date;
  public restartLimit : number = 5;

  /**
   * Kill the connection and reattempt it.
   */
  public restart (release : boolean = true, userInitiated : boolean = false)
    : void
  {
    if (!userInitiated)
    {
      // only increment the count if the restart wasn't deliberately initiated
      // by the user
      this.restartCount += 1;
    }
    this.lastRestart = new Date();
    if (this.restartCount >= this.restartLimit)
    {
      this.alerts.error(
        "Connection restart limit reached! Please close the app to try again.");
      this.be(MessagingState.Dead);
      return;
    }

    this.be(MessagingState.Restarting);
    if (release)
    {
      this.releaseConnection();
    }
    if (this.retryTimeout)
    {
      clearTimeout(this.retryTimeout);
    }
    this.startConnection();
  }

  /**
   * An incrementing ID to assign to the next outgoing message.
   */
  public idAllocator : number = 0;

  public get nextId () : number
  {
    return this.idAllocator++;
  }

  /**
   * Subscribe to incoming messages with a conversation ID matching the request
   * and if we receive more than `count` of them, throw an error. In the current
   * system, conversations should only have 1 message per-ID-per-direction, but
   * this method supports the idea of expecting 2 replies, etc.
   * @param {Message} request
   * @param {number}  count
   */
  private catchRepliesAfter (request : Message, count : number = 1) : void
  {
    const catchSub : Subscription
      = this.incoming.filter((m : Message) => m.id === request.id)
        .skip(count).subscribe(
          (extraneous : Message) => {
            console.error("Unexpected reply:", extraneous);
            throw new Error(
              "Server returned too many replies for message #" + request.id);
          }
        );
    this.subscription.add(catchSub);
  }

  /**
   * Return an observable of the first reply received. This observable will also
   * throw an error if no reply is received within a the time limit, assuring
   * that the observable will "resolve" in some manner and not simply "hang".
   * @param  {Message}                  message
   * @return {Observable<ReplyMessage>}
   */
  private firstReplyTo (message : Message) : Observable<ReplyMessage>
  {
    const timeout : number = message.meta.timeout || DEFAULT_REPLY_TIMEOUT;
    return this.incoming.first(
      (response : Message) => response.id === message.id)
      // .timeout(timeout, new ReplyTimeoutError(message, timeout))
      // TODO: less annoying version of the timeout
      .map((reply : Message) => ReplyMessage.create(reply, message))
      .catch((err : any, caught : Observable<Message>) => {
        if (err instanceof ReplyTimeoutError)
        {
          this.alerts.error(`The server could not respond to a request in time
            and may have encountered a fatal error.`);
          console.error("Timeout on message", message);
        }
        return (err instanceof EmptyError)
          ? Observable.throw(new MissingReplyError(message))
          : Observable.throw(err);
      });
  }

  /*
   * TODO: Set this up so it appears in the esdocs somehow (linked .md page?)
   *
   * The send() method has a few siblings (seen below it) that provide extra
   * behavior for ReplyVerifyingMessages. The overall relationships can be
   * summarized like so:
   *                                    (SUCCESS BEHAVIOR)
   *                        return Obs<Message>   return Obs<boolean>
   *                      +---------------------+---------------------.
   *     return same type |         send        |       resultOf      |
   * (FAILURE BEHAVIOR)   +---------------------+---------------------+
   *     throw obs. error |       converse      |      successful     |
   *                      .---------------------+---------------------.
   *
   * "return Obs<boolean>" indicates that it will return a true if the reply
   * validates appropriately and a false otherwise (though `successful` is
   * subclassed as Observable<true> since it will never false).
   */

  /**
   * Send a message to the connection and return an observable of the reply
   */
  public send (message : Message) : Observable<Message>
  {
    message.id = this.nextId;

    const logEntity : any
      = {id: 0, tag: 0, outgoing: true, timestamp: Date.now()};
    this.recentLog.add(Object.assign(logEntity, message));

    // We create an Observable using an anonynmous observer subscription
    // function. This differs from the old approach in that the message send
    // isn't triggered until the observable is subscribed to. (It's "COLDER.")
    return Observable.create((observer : Observer<Message>) => {
      this.connection.send(message);
      if (message.meta.allowUnboundedReplies)
      {
        console.warn("Sending message without bounds on replies!", message);
        this.repliesTo(message).subscribe(observer);
      }
      else if (message.expectsReply())
      {
        // Single reply expected
        this.catchRepliesAfter(message, 1);
        this.firstReplyTo(message).subscribe(observer);
      }
      else
      {
        // No replies expected
        this.catchRepliesAfter(message, 0);
        observer.complete();
      }
    }).share();
    // The observable is shared so that subsequent subscriptions don't resend
    // the initial message.
  }

  /**
   * Send a message to the connection, and if the provided message type does not
   * like the server's reply, throw an error. Otherwise return reply observable.
   */
  public converse (
    message : ReplyVerifyingMessage,
    error? : (response : Message) => void
  ) : Observable<Message>
  {
    return this.send(message).map((reply : Message) => {
      if (message.verifyReply(reply))
      {
        return reply;
      }
      else
      {
        if (error)
        {
          error(reply);
        }
        else
        {
          console.error("Reply failed request's validation:", message, reply);
          throw new BadReplyError(message, reply);
        }
      }
    });
  }

  /**
   * Send a message to the connection, and return an observable of whether or
   * not the message type deemed the reply successful.
   */
  public resultOf (message : ReplyVerifyingMessage, notify : boolean = false)
    : Observable<boolean>
  {
    return this.send(message).map(
      (reply : Message) => {
        const verified : boolean = message.verifyReply(reply);
        if (!verified)
        {
          const reasonText : string = (reply as any).reason;
          if (reasonText && notify)
          {
            this.alerts.error(escapeForHTML(reasonText));
          }
        }
        return verified;
      });
  }

  /**
   * Return an observable that sends true if this messages gets a valid reply
   * or throws an error if the reply is invalid.
   * This is sort of like "asserting" the message was successful,
   * @param  {ReplyVerifyingMessage} message [description]
   * @return {Observable<true>}
   */
  public successful (message : ReplyVerifyingMessage) : Observable<true>
  {
    function verifyOrError (r : Message) : true
    {
      if (!message.verifyReply(r))
      {
        throw new BadReplyError(message, r);
      }
      return true;
    }

    return this.converse(message).map(verifyOrError);
  }

  /**
   * All the tag numbers that haved been received by some service.
   */
  private handledTags : number[] = [];

  /**
   * Retrieve an observable that emits all {@link Message}s of a particular tag.
   * Since {@link Tagged} matches (statically) {@link Message}, we can pass in
   * the message *classes* themselves.
   */
  public receive<M extends Message> (
    filter : Tagged | number,
    ignoreConversational : boolean = false
  ) : Observable<M>
  {
    const tag : number = (typeof filter === "number") ? filter : filter.tag;
    // Record that someone is handling this tag (mostly for debugging purposes)
    this.handledTags.push(tag);

    return this.incoming.filter(
      (message : Message) =>
        (message.tag === tag)
        && (!ignoreConversational || message.id < 0)) as Observable<M>;
  }

  /**
   * Retrieve an observable that emits all {@link Message}s of a particular tag.
   * Since {@link Tagged} matches (statically) {@link Message}, we can pass in
   * the message *classes* themselves.
   */
  public receiveAnyOf<M extends Message> (
    tags : number[]
  ) : Observable<M>
  {
    // Record that someone is handling this tag (mostly for debugging purposes)
    this.handledTags = this.handledTags.concat(tags);

    return this.incoming.filter(
      (message : Message) => (tags.indexOf(message.tag) > -1)) as Observable<M>;
  }

  /**
   * Collect all replies (by ID) to a particular message
   * @param idOrMsg an ID or a message with an ID
   */
  public repliesTo (idOrMsg : number | Identifiable) : Observable<Message>
  {
    const id : number = (typeof idOrMsg === "number") ? idOrMsg : idOrMsg.id;
    return this.incoming.filter((m : Message) => m.id === id);
  }

  /**
   * Obtain the next message of a particular type received within a given span
   * @param filter tag to receive
   * @param timeLimit number of milliseconds to wait for message
   */
  public nextOfType (filter : Tagged | number, timeLimit? : number)
    : Observable<Message>
  {
    const tag : number = (typeof filter === "number") ? filter : filter.tag;
    if (timeLimit === undefined)
    {
      return this.incoming.first((m : Message) => m.tag === tag);
    }
    return this.incoming.first((m : Message) => m.tag === tag)
      .timeout(timeLimit); // TODO: maybe catch the timeout error?
  }

  /**
   * List of incoming {@link Message}s that have negative indices and have
   * never had their tag received by a service.
   */
  public unclaimed : Message[] = [];

  /**
   * Do something for all {@link Message}s that aren't replies or receive()-d.
   */
  private nonconversational (message : Message) : void
  {
    // NOTE: This is if we want to track/debug incoming messages that aren't
    // part of a conversation (e.g. if the server isn't matching IDs or our
    // filters aren't catching the tag types they need to).
    console.warn(
      "Server message did not have a local listener:", message);
    this.unclaimed.push(message);
  }

  /**
   * Scan the unclaimed messages for a particular tag, and return all messages
   * of that type in an observable stream.
   * @param tag tag to filter by
   */
  public receiveMissed (tag : number) : Observable<Message>
  {
    return Observable.from(this.unclaimed.filter(
      (m : Message) => Message.isA(m, tag)));
  }

  public downloadFile (token : string, secret : string, type : string)
    : Observable<SocketFile>
  {
    return (new DownloadSocket(this.socketUrl, token, secret, type)).completion;
  }

  public sendSideChannelOneOff (message : Message) : Observable<boolean>
  {
    const connection : WebSocketConnection = new WebSocketConnection(
      this.socketUrl,
      new DisconnectUpstreamMessage(-1),
      -1);
    let localId : number = 1;
    const negotiation : Observable<boolean> =
      this.protocolVersionService.negotiate((m : Message) => {
        m.meta.timeout = NEGOTIATION_TIMEOUT;
        m.id = localId++;
        connection.send(m);
        return connection.incoming.first(
          (response : Message) => response.id === m.id)
          .map((reply : Message) => ReplyMessage.create(reply, message));
      });
    return negotiation.map(
      (success : boolean) => {
        if (success)
        {
          message.id = localId++;
          connection.send(message);
          setTimeout(() => connection.close(), 2000);
        }
        return success;
      }
    );;
  }
}



// WEBPACK FOOTER //
// ./~/angular-router-loader/src!./app/server/messaging.service.ts